import type { Deuda, DeudaPago, EstadoDeuda } from '../lib/supabase';

/** Un pago cuenta si no está anulado. Para pagos con movimiento, la anulación
 * se deriva del movimiento (movimiento_anulado); para historia previa, del propio pago. */
export function pagoVigente(p: DeudaPago): boolean {
  if (p.movimiento_id) return !p.movimiento_anulado;
  return !p.anulado;
}

/** Fecha de vencimiento de la cuota k (1-based), derivada de mes_primer_vencimiento + dia_vencimiento.
 * En meses cortos, el día se recorta al último del mes.
 *
 * Los componentes de mesPrimerVencimiento (ISO "YYYY-MM-DD") se extraen del string directamente,
 * sin pasar por `new Date(mesPrimerVencimiento)`: ese parseo trata la fecha como UTC medianoche,
 * y leerla luego con getters locales (`getMonth`/`getDate`) la corre un día hacia atrás
 * en cualquier timezone con offset negativo (ej. America/Santiago) — desplazando el 1 de
 * enero a diciembre del año anterior y rompiendo el cálculo del mes esperado. */
export function vencimientoCuota(mesPrimerVencimiento: string, diaVencimiento: number, k: number): Date {
  const [anioInicio, mesInicio] = mesPrimerVencimiento.split('-').map(Number);
  const mesIndiceGlobal = (mesInicio - 1) + (k - 1); // 0-based, k=1 → mes de la primera cuota
  const anio = anioInicio + Math.floor(mesIndiceGlobal / 12);
  const mes = ((mesIndiceGlobal % 12) + 12) % 12;
  const ultimoDia = new Date(anio, mes + 1, 0).getDate();
  return new Date(anio, mes, Math.min(diaVencimiento, ultimoDia));
}

/** Valor de cada cuota, derivado de monto_total y numero_cuotas (redondeo hacia abajo;
 * la última cuota absorbe el resto — ver `montoEsperadoCuota`). Única fuente de verdad
 * para que el preview del formulario y el valor guardado nunca diverjan. */
export function calcularValorCuota(montoTotal: number, numeroCuotas: number): number {
  if (numeroCuotas <= 0) return 0;
  return Math.floor(montoTotal / numeroCuotas);
}

/** Monto esperado de la cuota k: valor_cuota fijo, salvo la última cuota, que absorbe
 * la diferencia cuando monto_total no es divisible exacto por numero_cuotas. */
function montoEsperadoCuota(deuda: Pick<Deuda, 'monto_total' | 'numero_cuotas' | 'valor_cuota'>, k: number): number {
  if (k < deuda.numero_cuotas) return Number(deuda.valor_cuota);
  return Number(deuda.monto_total) - Number(deuda.valor_cuota) * (deuda.numero_cuotas - 1);
}

export function calcularEstadoDeuda(deuda: Deuda, pagos: DeudaPago[], hoy: Date = new Date()): {
  pagado: number; saldo: number; cuotasPagadas: number; cuotasAtrasadas: number; estado: EstadoDeuda;
} {
  const vigentes = pagos.filter(pagoVigente);
  const pagado = vigentes.reduce((s, p) => s + Number(p.monto), 0);
  const saldo = Math.max(0, Number(deuda.monto_total) - pagado);

  let estado: EstadoDeuda = 'activa';
  if (deuda.anulado) estado = 'anulada';
  else if (saldo <= 0) estado = 'saldada';

  // La cuota k está cubierta cuando lo pagado alcanza la suma de los montos esperados de 1..k
  // (la última incluye el resto, así que su acumulado es monto_total).
  // Cuotas atrasadas: cuotas cuyo vencimiento ya pasó y que aún no están cubiertas.
  let cuotasPagadas = 0;
  let cuotasAtrasadas = 0;
  let acumuladoEsperado = 0;
  for (let k = 1; k <= deuda.numero_cuotas; k++) {
    acumuladoEsperado += montoEsperadoCuota(deuda, k);
    const cubierta = pagado >= acumuladoEsperado;
    if (cubierta) cuotasPagadas++;
    const vencida = vencimientoCuota(deuda.mes_primer_vencimiento, deuda.dia_vencimiento, k) < hoy;
    if (estado === 'activa' && vencida && !cubierta) cuotasAtrasadas++;
  }
  return { pagado, saldo, cuotasPagadas, cuotasAtrasadas, estado };
}

export type EstadoCuota = 'pagada' | 'parcial' | 'pendiente' | 'atrasada';

export interface CuotaDesglosada {
  numeroCuota: number;
  montoEsperado: number;
  montoPagado: number;
  vencimiento: Date;
  estado: EstadoCuota;
  pagos: DeudaPago[];
}

/** Desglosa la deuda en sus cuotas 1..N con su estado individual (para la cronología del detalle).
 * Cada pago ya trae su numero_cuota asignado explícitamente (al registrarlo o al cargar historia
 * previa), así que el monto pagado de una cuota es la suma de sus pagos vigentes. */
export function desglosarCuotas(deuda: Deuda, pagos: DeudaPago[], hoy: Date = new Date()): CuotaDesglosada[] {
  const vigentes = pagos.filter(pagoVigente);
  const cuotas: CuotaDesglosada[] = [];

  for (let k = 1; k <= deuda.numero_cuotas; k++) {
    const pagosCuota = vigentes.filter(p => p.numero_cuota === k);
    const montoPagado = pagosCuota.reduce((s, p) => s + Number(p.monto), 0);
    const montoEsperado = montoEsperadoCuota(deuda, k);
    const vencimiento = vencimientoCuota(deuda.mes_primer_vencimiento, deuda.dia_vencimiento, k);

    let estado: EstadoCuota;
    if (montoPagado >= montoEsperado) estado = 'pagada';
    else if (montoPagado > 0) estado = 'parcial';
    else if (vencimiento < hoy) estado = 'atrasada';
    else estado = 'pendiente';

    cuotas.push({ numeroCuota: k, montoEsperado, montoPagado, vencimiento, estado, pagos: pagosCuota });
  }
  return cuotas;
}
